/**
 * A single row in the network SLA summary table. Shows NYR units and percentage received grouped by warehouse.
 */

import React from "react"
import PropTypes from "prop-types"
import { TableRow, TableCell } from "@amzn/meridian/table"
import Text from "@amzn/meridian/text"

const NetworkSLATempSummaryTableRow = props => {
    const { summaryData } = props

    // Handle new data structure - summaryData now has tempZones as an array
    const tempZones = summaryData.tempZones || [];
    const warehouseName = summaryData.warehouseName || summaryData.warehouseId;

    return (
        <>
            {tempZones.map((tempZone, index) => {
                const isLastRow = index === tempZones.length - 1
                return (
                    <TableRow key={`${warehouseName}-${tempZone.tempZone}`} backgroundColor={isLastRow ? "neutral-100" : undefined}>
                        {index === 0 ?
                            <TableCell alignmentHorizontal={"left"} rowSpan={tempZones.length}>
                                <Text><strong>{warehouseName}</strong></Text>
                            </TableCell>
                            : null
                        }
                        <TableCell>
                            <Text>{tempZone.tempZone}</Text>
                            {isLastRow && (
                                <div style={{ borderTop: '1px solid #d1d5db', paddingTop: '8px', marginTop: '8px' }}>
                                    <Text><strong>Total</strong></Text>
                                </div>
                            )}
                        </TableCell>
                        <TableCell>
                            <Text>
                                {tempZone.totalExpected?.toLocaleString() || '0'}
                            </Text>
                            {isLastRow && (
                                <div style={{ borderTop: '1px solid #d1d5db', paddingTop: '8px', marginTop: '8px' }}>
                                    <Text><strong>{summaryData.siteTotal?.totalExpected?.toLocaleString() || '0'}</strong></Text>
                                </div>
                            )}
                        </TableCell>
                        <TableCell>
                            <Text>
                                {tempZone.totalReceived?.toLocaleString() || '0'}
                            </Text>
                            {isLastRow && (
                                <div style={{ borderTop: '1px solid #d1d5db', paddingTop: '8px', marginTop: '8px' }}>
                                    <Text><strong>{summaryData.siteTotal?.totalReceived?.toLocaleString() || '0'}</strong></Text>
                                </div>
                            )}
                        </TableCell>
                        <TableCell>
                            <Text>
                                {tempZone.totalNYR?.toLocaleString() || '0'}
                            </Text>
                            {isLastRow && (
                                <div style={{ borderTop: '1px solid #d1d5db', paddingTop: '8px', marginTop: '8px' }}>
                                    <Text><strong>{summaryData.siteTotal?.totalNYR?.toLocaleString() || '0'}</strong></Text>
                                </div>
                            )}
                        </TableCell>
                        <TableCell>
                            <Text>
                                {tempZone.totalNYRPastSLA?.toLocaleString() || '0'}
                            </Text>
                            {isLastRow && (
                                <div style={{ borderTop: '1px solid #d1d5db', paddingTop: '8px', marginTop: '8px' }}>
                                    <Text><strong>{summaryData.siteTotal?.totalNYRPastSLA?.toLocaleString() || '0'}</strong></Text>
                                </div>
                            )}
                        </TableCell>
                        <TableCell>
                            <Text>
                                {tempZone.percentPastSLA || '0'}%
                            </Text>
                            {isLastRow && (
                                <div style={{ borderTop: '1px solid #d1d5db', paddingTop: '8px', marginTop: '8px' }}>
                                    <Text><strong>{summaryData.siteTotal?.percentPastSLA || '0'}%</strong></Text>
                                </div>
                            )}
                        </TableCell>
                        <TableCell>
                            <Text>
                                {tempZone.percentReceived || '0'}%
                            </Text>
                            {isLastRow && (
                                <div style={{ borderTop: '1px solid #d1d5db', paddingTop: '8px', marginTop: '8px' }}>
                                    <Text><strong>{summaryData.siteTotal?.percentReceived || '0'}%</strong></Text>
                                </div>
                            )}
                        </TableCell>
                    </TableRow>
                )
            })}
        </>
    )
}

NetworkSLATempSummaryTableRow.propTypes = {
    summaryData: PropTypes.shape({
        warehouseName: PropTypes.string,
        warehouseId: PropTypes.string,
        tempZones: PropTypes.arrayOf(PropTypes.shape({
            tempZone: PropTypes.string.isRequired,
            totalExpected: PropTypes.number,
            totalReceived: PropTypes.number,
            totalNYR: PropTypes.number,
            totalNYRPastSLA: PropTypes.number,
            percentReceived: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            percentPastSLA: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        })).isRequired,
        siteTotal: PropTypes.shape({
            totalExpected: PropTypes.number,
            totalReceived: PropTypes.number,
            totalNYR: PropTypes.number,
            totalNYRPastSLA: PropTypes.number,
            percentReceived: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            percentPastSLA: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        })
    }).isRequired
}

export default React.memo(NetworkSLATempSummaryTableRow)
